/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useCallback, useEffect } from 'react';
import { matchPath } from 'react-router';
import { isValidCompanyTrackerRoot } from './utils';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { AppRouteInterface } from '../../types/routes';
import { CompanyTrackerInitiativeRoute } from './CompanyTrackerInitiativeRoute';
import { RouteErrorBoundary } from '@features/error';
import UTrModal from '@features/utr-modal';
import Dashboard, { DashboardSection } from '@g17eco/molecules/dashboard';
import { getInitiativeTree } from '../../selectors/initiativeTree';
import { OrganizationSwitcherRoute } from '../OrganizationSwitcherRoute';
import { companyTrackerRouteMatcher } from '../appRootMatcher';
import { RedirectToCompanyTracker } from './RedirectToCompanyTracker';
import { getDefaultConfig } from '../../config/app-config';
import { AgreementChecker } from '@features/agreement';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { loadSurveyList, selectMostRecentSurvey } from '../../slice/initiativeSurveyListSlice';
import './styles.scss';
import { companyTrackerReportRoutes, companyTrackerRoutes } from './routes';
import { getHydratedRoutes } from '../../constants/routeComponents';

const branding = getDefaultConfig().branding.ctlApp;
const companyTrackerAppRoute: AppRouteInterface = {
  appName: branding.name,
  appIcon: branding.logo,
  routes: [
    companyTrackerReportRoutes.COMPANY_TRACKER_SURVEY,
    companyTrackerRoutes.COMPANY_TRACKER_LIST,
    companyTrackerRoutes.SUMMARY,
    companyTrackerRoutes.DOWNLOADS,
    companyTrackerRoutes.ADMIN_SETTINGS,
  ],
  hasReportingLevels: true,
}

const pathMatch = `${companyTrackerRoutes.COMPANY_TRACKER.path}/:anyPage?/:initiativeId?`;

// Extract all routes that allow initiative change
const routesWithSwitch = Object.values(companyTrackerRoutes).filter(r => r.allowInitiativeChange);
const routes = getHydratedRoutes(companyTrackerRoutes);

/**
 * Company Tracker App entry point
 */
export const CompanyTrackerRoute = () => {

  const history = useHistory();
  const dispatch = useAppDispatch();
  const match = useRouteMatch<{ initiativeId: string }>({ path: pathMatch });
  const initiativeId = match?.params?.initiativeId;

  const initiativeTree = useAppSelector(getInitiativeTree);
  const currentSurvey = useAppSelector(selectMostRecentSurvey);
  useEffect(() => {
    if (initiativeId) {
      dispatch(loadSurveyList(initiativeId));
    }
  }, [initiativeId, dispatch]);

  const redirectRoute = routesWithSwitch.find(route => matchPath(history.location.pathname, route))
    ?? companyTrackerRoutes.SUMMARY;

  const redirectHandler = useCallback((initiativeId: string, rootAppPath?: string) => {

    if (initiativeId) {
      if (rootAppPath && !isValidCompanyTrackerRoot(rootAppPath)) {
        // @TODO fix the routing for MAT
        // Must be wrong root app path, try to redirect, rather than fail (MAT access etc.)
        history.push(`/${rootAppPath}/${initiativeId}`)
        return;
      }

      history.push(companyTrackerRouteMatcher.generateUrl(initiativeId, rootAppPath))
    }
  }, [history]);

  const defaultParams: AppRouteInterface['defaultParams'] = {
    initiativeId,
    surveyId: currentSurvey?._id,
  }

  return (
    <RouteErrorBoundary>
      <OrganizationSwitcherRoute
        appRoute={{
          ...companyTrackerAppRoute,
          defaultParams
        }}
        routeMatcher={companyTrackerRouteMatcher}
        changeReportingLevelRoute={redirectRoute}
        redirectHandler={redirectHandler}
        initiativeTree={initiativeTree}
        pathMatch={pathMatch}>
        <AgreementChecker />
        <Switch>
          <Route path={routes.COMPANY_TRACKER_SURVEY_REDIRECTOR.path}>
            <RedirectToCompanyTracker />
          </Route>

          {/*"/company-tracker/reports/:initiativeId?",*/}
          <Route {...routes.COMPANY_TRACKER_LIST} />
          <Route {...routes.COMPANY_TRACKER_SURVEY} />
          {/* Normal Initiative*/}
          <Route path={pathMatch} component={CompanyTrackerInitiativeRoute} />
          <Route>
            <Dashboard>
              <DashboardSection icon={'fa-tasks'} title='Company Tracker'>
                <p>You currently don't have any Companies associated with this account.</p>
                <p>Please contact our customer success team to set up your account.</p>
              </DashboardSection>
            </Dashboard>
          </Route>
        </Switch>
        <UTrModal />
      </OrganizationSwitcherRoute>
    </RouteErrorBoundary>
  )
}
